/**
 * ExplanationPanelPage — the in-app physician explanation panel alongside a signed report
 * (/ai-explanation/:crId). It reads the persisted explanation for a compte-rendu, renders the bilingual
 * FR/AR block + the ALWAYS-ON governed disclaimer, and lets a Biologiste generate / regenerate it.
 *
 * Cloud-only by design. The module's /api/v1 routes are absent on a local install, so the read probe
 * 404s → we render a clean "not available on this install" state (the signed report itself is never
 * blocked). On a cloud install the data layer degrades gracefully: the read returns `null` (no
 * explanation yet), and generate/regenerate NEVER throw — they return a tagged result, so a missing
 * key / quota / outage surfaces as the "indisponible" state, never an error.
 *
 * Full 5-state DoD off the read query: hors-ligne (cache-aware), chargement (skeleton), erreur (a 404
 * becomes the unavailable state; everything else is a retryable ErrorState), vide (no explanation →
 * generate CTA), sans-droit (route-level ForbiddenState + per-action useCan). Keyboard-first: every
 * action is a real <Button> in tab order; the CR-id mono island stays LTR under RTL.
 */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  OfflineState,
  useToast,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { AIX_CAPS } from "../caps";
import { isModuleUnmounted, useExplication, useGenerate, useRegenerate } from "../api";
import type { GenerationResult } from "../types";
import { PageHeader } from "../components/PageHeader";
import { DisclaimerBlock } from "../components/DisclaimerBlock";
import { BilingualBlock } from "../components/BilingualBlock";
import { IndisponibleState } from "../components/IndisponibleState";
import { toIndisponible } from "../lib";

function Screen({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>;
}

export function ExplanationPanelPage() {
  const { t } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const params = useParams<{ crId: string }>();
  const crId = params.crId ?? null;
  const online = useUiStore((s) => s.online);

  const canRead = useCan(AIX_CAPS.read);
  const canGenerate = useCan(AIX_CAPS.generate);
  const canRegenerate = useCan(AIX_CAPS.regenerate);

  const query = useExplication(crId, canRead && online);
  const generate = useGenerate(crId);
  const regenerate = useRegenerate(crId);

  // The last generate/regenerate tag — drives the graceful "indisponible" state (the row stays absent
  // when the service is disabled/errored, so this is the only signal that a generation was attempted).
  const [lastResult, setLastResult] = useState<GenerationResult | null>(null);

  const back = (
    <Button variant="ghost" leadingIcon="‹" onClick={() => navigate("/ai-explanation")}>
      {t("aix.panel.back")}
    </Button>
  );

  const header = (
    <PageHeader
      title={t("aix.panel.title")}
      subtitle={crId ? t("aix.panel.subtitle", { cr: crId }) : undefined}
      actions={back}
    />
  );

  const onGenerate = () => {
    generate.mutate(undefined, {
      onSuccess: (res) => {
        setLastResult(res);
        if (res.status === "ok") toast.success(t("aix.generated.ok"));
      },
    });
  };

  const onRegenerate = () => {
    regenerate.mutate(undefined, {
      onSuccess: (res) => {
        setLastResult(res);
        if (res.status === "ok") toast.success(t("aix.regenerated.ok"));
      },
    });
  };

  // ── sans-droit (also enforced route-level by ProtectedRoute) ───────────────────
  if (!canRead) return <ForbiddenState requiredCapabilities={[AIX_CAPS.read]} />;

  // ── hors-ligne ─────────────────────────────────────────────────────────────────
  if (!online && !query.data) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void query.refetch()} cached={!!query.data} />
      </Screen>
    );
  }

  // ── chargement ───────────────────────────────────────────────────────────────────
  if (query.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={4} />
      </Screen>
    );
  }

  // ── erreur — a 404 means the cloud-only module is NOT mounted on this install ──────
  if (query.isError) {
    if (isModuleUnmounted(query.error)) {
      return (
        <Screen>
          {header}
          <EmptyState
            glyph="☁"
            title={t("aix.unavailable.title")}
            description={t("aix.unavailable.desc")}
          />
          <DisclaimerBlock />
        </Screen>
      );
    }
    return (
      <Screen>
        {header}
        <ErrorState
          message={query.error.message}
          code={query.error.code}
          onRetry={() => void query.refetch()}
        />
      </Screen>
    );
  }

  const explication = query.data ?? null;
  const indisponible = toIndisponible(lastResult);
  const generating = generate.isPending;
  const regenerating = regenerate.isPending;

  return (
    <Screen>
      <PageHeader
        title={t("aix.panel.title")}
        subtitle={crId ? t("aix.panel.subtitle", { cr: crId }) : undefined}
        badge={
          explication ? (
            <Badge tone="info" glyph="✦">
              {t("aix.action.generate")}
            </Badge>
          ) : undefined
        }
        actions={
          <div style={{ display: "flex", gap: "var(--nv-s-2)", flexWrap: "wrap" }}>
            {back}
            {explication && canRegenerate && (
              <Button
                variant="secondary"
                loading={regenerating}
                onClick={onRegenerate}
                leadingIcon="↻"
              >
                {regenerating ? t("aix.action.regenerating") : t("aix.action.regenerate")}
              </Button>
            )}
          </div>
        }
      />

      {/* Always-on governed disclaimer — present on every state of the panel. */}
      <DisclaimerBlock serverDisclaimer={lastResult?.disclaimer ?? null} />

      {explication ? (
        <Card
          title={t("aix.block.title")}
          actions={
            <span
              className="nv-mono nv-ltr-island"
              style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-steel)" }}
            >
              {t("aix.panel.model")}: {explication.modele ?? t("aix.na")}
            </span>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <BilingualBlock texte={explication.texte} />
            <p style={{ margin: 0, color: "var(--nv-slate)", fontSize: "var(--nv-fs-caption)" }}>
              {t("aix.panel.generatedNote")}
            </p>
          </div>
        </Card>
      ) : indisponible ? (
        <Card>
          <IndisponibleState
            status={indisponible.status}
            reason={indisponible.reason}
            onRetry={canGenerate ? onGenerate : undefined}
            retrying={generating}
          />
        </Card>
      ) : (
        <Card>
          <EmptyState
            glyph="✦"
            title={t("aix.none.title")}
            description={t("aix.none.desc")}
            action={
              canGenerate ? (
                <Button loading={generating} onClick={onGenerate} leadingIcon="✦">
                  {generating ? t("aix.action.generating") : t("aix.action.generate")}
                </Button>
              ) : undefined
            }
          />
        </Card>
      )}
    </Screen>
  );
}
